import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Combobox-like field:
 * - shows a select with predefined options
 * - selecting "Other / Custom" reveals a free-text input
 * - if the current value is not in the options it is treated as custom
 */
export default function ComboTextField({ label, required, value, onChange, options, placeholder, testId }) {
  const [isCustom, setIsCustom] = useState(() => Boolean(value) && !options.includes(value));

  useEffect(() => {
    if (value && !options.includes(value)) {
      setIsCustom(true);
    }
  }, [value, options]);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === "__other__") {
      setIsCustom(true);
      onChange("");
    } else {
      setIsCustom(false);
      onChange(v);
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-1.5">
          {label}{required && <span className="text-[#2BA6A6]"> *</span>}
        </label>
      )}
      {!isCustom ? (
        <div className="relative">
          <select
            value={options.includes(value) ? value : ""}
            onChange={handleSelect}
            required={required}
            className="w-full px-3 py-2 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6] appearance-none pr-8"
            data-testid={testId}
          >
            <option value="">{placeholder || "Select…"}</option>
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
            <option value="__other__">— Other (type your own) —</option>
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            placeholder="Type custom value…"
            className="flex-1 px-3 py-2 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
            data-testid={testId}
            autoFocus
          />
          <button
            type="button"
            onClick={() => { setIsCustom(false); onChange(""); }}
            className="text-xs text-[#2BA6A6] hover:text-[#001F3F] underline whitespace-nowrap"
          >
            ← Use list
          </button>
        </div>
      )}
    </div>
  );
}
